import { Plus, Trash2 } from "lucide-react";
import { COURT_LEVELS, newPrepClaim, prepClaimAmount, prepClaimsTotal, prepFeesFor } from "@/lib/prepClaims";
import { findBillingFee } from "@/lib/billingFeeTable";
import { formatMoney } from "./BillingReportPanel";

const input = "w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300";
const label = "mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-400";

// The week's preparation claims: prep and lump sum fees claimed for clients who did not
// necessarily appear this week (see src/lib/prepClaims.js).
export default function PrepClaimsPanel({ lines = [], onChange }) {
  const update = (id, patch) => onChange(lines.map(line => (line.id === id ? { ...line, ...patch } : line)));
  const remove = (id) => onChange(lines.filter(line => line.id !== id));
  const add = () => onChange([...lines, newPrepClaim()]);

  return (
    <section className="space-y-3 rounded-2xl border border-purple-200 bg-white p-4 shadow-sm print:hidden">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Preparation claims ({lines.length})</h3>
          <p className="mt-0.5 text-xs text-slate-500">Prep and lump sum fees claimed this week, such as for aid approved this week. They count in the week&apos;s total and print with it.</p>
        </div>
        <div className="flex items-center gap-3">
          {lines.length > 0 && <span className="text-sm font-bold text-purple-800">Total {formatMoney(prepClaimsTotal(lines))}</span>}
          <button type="button" onClick={add} className="flex items-center gap-1.5 rounded-xl border border-purple-300 bg-purple-50 px-3 py-2 text-xs font-bold text-purple-800 hover:bg-purple-100">
            <Plus className="h-3.5 w-3.5" /> Add prep claim
          </button>
        </div>
      </div>

      {lines.length === 0 && (
        <p className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-400">
          No prep claims this week. Add one here, from an approved matter on Aid Pending, or from the ATLAS comparison.
        </p>
      )}

      {lines.map(line => {
        const groups = prepFeesFor(line.courtLevel);
        const listed = findBillingFee(line.feeKey)?.amount ?? null;
        const amount = prepClaimAmount(line);
        const missing = !line.client.trim() || amount == null;
        return (
          <div key={line.id} className={`grid gap-3 rounded-xl border p-3 sm:grid-cols-2 lg:grid-cols-12 ${missing ? "border-amber-300 bg-amber-50/50" : "border-slate-200 bg-slate-50/50"}`}>
            <div className="lg:col-span-2">
              <label className={label} htmlFor={`prep-client-${line.id}`}>Client</label>
              <input id={`prep-client-${line.id}`} className={input} value={line.client} placeholder="SURNAME, First" onChange={e => update(line.id, { client: e.target.value })} />
            </div>
            <div className="lg:col-span-2">
              <label className={label} htmlFor={`prep-grant-${line.id}`}>Grant</label>
              <input id={`prep-grant-${line.id}`} className={input} value={line.grant} placeholder="26A000000/0" onChange={e => update(line.id, { grant: e.target.value })} />
            </div>
            <div className="lg:col-span-3">
              <label className={label} htmlFor={`prep-level-${line.id}`}>Court level</label>
              <select id={`prep-level-${line.id}`} className={input} value={line.courtLevel} onChange={e => update(line.id, { courtLevel: e.target.value, feeKey: "", amount: undefined })}>
                {COURT_LEVELS.map(level => <option key={level} value={level}>{level}</option>)}
              </select>
            </div>
            <div className="lg:col-span-3">
              <label className={label} htmlFor={`prep-fee-${line.id}`}>Prep fee</label>
              <select id={`prep-fee-${line.id}`} className={input} value={line.feeKey} onChange={e => update(line.id, { feeKey: e.target.value, amount: undefined })}>
                <option value="">Choose a prep fee…</option>
                {groups.map(group => (
                  <optgroup key={group.table} label={group.table}>
                    {group.fees.map(fee => <option key={fee.key} value={fee.key}>{fee.name} – {formatMoney(fee.amount)}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="lg:col-span-2">
              <label className={label} htmlFor={`prep-amount-${line.id}`}>Amount</label>
              <div className="flex items-center gap-1">
                <div className="flex flex-1 items-center rounded-lg border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-purple-300">
                  <span className="pl-2.5 text-sm text-slate-400">$</span>
                  <input
                    id={`prep-amount-${line.id}`}
                    type="number" min="0" step="0.01" inputMode="decimal"
                    className="w-full bg-transparent px-1.5 py-1.5 text-sm focus:outline-none"
                    value={line.amount ?? (listed != null ? String(listed) : "")}
                    placeholder="Choose fee"
                    onChange={e => update(line.id, { amount: e.target.value === "" ? undefined : e.target.value })}
                  />
                </div>
                <button type="button" onClick={() => remove(line.id)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" title="Remove this prep claim" aria-label="Remove this prep claim">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {line.amount != null && listed != null && Number(line.amount) !== listed && (
                <p className="mt-1 text-xs text-amber-700">Listed fee {formatMoney(listed)}</p>
              )}
            </div>
            <div className="sm:col-span-2 lg:col-span-12">
              <input className={input} value={line.note} placeholder="Note (optional), e.g. aid approved 21 Oct" onChange={e => update(line.id, { note: e.target.value })} aria-label="Note" />
            </div>
          </div>
        );
      })}
    </section>
  );
}
